import { useCallback, useEffect, useMemo, useRef, type FC } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type DeepPartial,
  type FieldValues,
  type UseFieldArrayRemove,
  type UseFormGetValues,
} from "react-hook-form";
import { InputMask } from "primereact/inputmask";
import styled from "styled-components";

import { useRootSelector } from "@ds/hooks/redux-hooks";
import { type OnDeepPartialSubmitType } from "@ds/hooks/redux-forms-hooks";

import {
  DetailsCard,
  DetailsCardEditActionsMenu,
  EditableDetailsCardDecorator,
  type EditableDetailsCardChildProps,
} from "@ds/components/details-view";
import { Button, ButtonType, ButtonActionType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { renderErrorMessage } from "@ds/components/forms/labels";
import { MultipleValuesLabel, UnassignedLabel } from "@ds/components/forms/labels/common";
import {
  ValidationInputWrapper,
  ValidationTitle,
  ValidationLabelContentWrapper,
} from "@ds/components/forms/labels/styles";
import { IconType } from "@ds/components/icons";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";

import { selectLocationById, selectLocationsError, selectLocationsIsLoading } from "../../redux/selectors";
import { updateValidationSchema } from "../../utils/validation-schema";
import {
  contactFieldValueOrPlaceholder,
  getContactPhoneDisplayValue,
  getContactsArrayFromLocation,
  mapLocationContactsForForm,
  normalizeContactsForSubmit,
} from "../../utils/contacts-helpers";

const contactsYupResolver = yupResolver(updateValidationSchema.contacts);

////////////////////////////////////////////////////////////
// Styled Components
////////////////////////////////////////////////////////////

const ContactEditDeleteSlot = styled.div`
  grid-column: 5;
  grid-row: 2;
  display: flex;
  align-items: center;
  min-height: 35px;

  & > :is(button) {
    color: var(--red-color);
  }
`;

const ContactEditErrorSlot = styled.div<{ $col: number }>`
  grid-column: ${({ $col }) => $col};
  grid-row: 3;
  min-height: 18px;
  min-width: 0;
  width: 100%;
  font-size: 11px;
  font-weight: 600;
  color: var(--red-color);
`;

const ContactEditInputSlot = styled.div<{ $col: number }>`
  grid-column: ${({ $col }) => $col};
  grid-row: 2;
  min-height: 35px;
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;

  & > * {
    width: 100%;
    min-width: 0;
  }
`;

const ContactEditLabelSlot = styled.div<{ $col: number }>`
  grid-column: ${({ $col }) => $col};
  grid-row: 1;
  padding-bottom: 4px;
  min-width: 0;
  width: 100%;
`;

/* Grid: row 1 = labels, row 2 = inputs + delete (always aligned), row 3 = errors.
 * Four field columns expand uniformly; delete column is auto-sized. */
const ContactEditRowGrid = styled.div`
  align-items: flex-end;
  border-bottom: 1px solid var(--textColor010);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
  grid-template-rows: auto 35px 20px;
  gap: 0 16px;
  width: 100%;
`;

const ContactViewCell = styled.div`
  flex: 1;
  min-width: 120px;
  overflow: hidden;
  word-break: break-all;
`;

const ContactViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContactViewRow = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--textColor010);
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 0 16px 0;
`;

////////////////////////////////////////////////////////////
// Component Implementation
////////////////////////////////////////////////////////////

/** Form model matches `updateValidationSchema.contacts` only — not full `Locality` — so yup resolver
 *  does not replace values with a partial object that drops `contacts` / breaks `useFieldArray`. */
type LocationContactsCardFormValues = FieldValues & {
  contacts: LocationContact[];
};

const defaultContact: LocationContact = {
  email: "",
  name: "",
  phone: "",
  title: "",
};

/** Prefer `contacts` from the normalized Redux row when `contactSourceLocationId` is set; else `location` prop. */
function useLocationContactsRaw(location: Locality, contactSourceLocationId?: number): LocationContact[] {
  const storeLocation = useRootSelector(
    state =>
      typeof contactSourceLocationId === "number" && Number.isFinite(contactSourceLocationId)
        ? selectLocationById(state, contactSourceLocationId)
        : undefined,
    (prev, next) => prev?.contacts === next?.contacts,
  );

  return useMemo((): LocationContact[] => {
    if (storeLocation?.contacts !== undefined) {
      return storeLocation.contacts;
    }
    return getContactsArrayFromLocation(location) ?? [];
  }, [storeLocation, location]);
}

const LocationDetailsContactRow: FC<{
  contactIndex: number;
  control: Control<LocationContactsCardFormValues>;
  onDelete: UseFieldArrayRemove;
}> = ({ contactIndex, control, onDelete }) => {
  const fieldPrefix = `contacts.${contactIndex}` as const;
  const contactName = useWatch({ control, name: `${fieldPrefix}.name` });
  const deleteAriaLabel =
    typeof contactName === "string" && contactName.trim()
      ? `Delete contact ${contactName.trim()}`
      : `Delete contact ${contactIndex + 1}`;

  const handleDeleteRow = useCallback(() => {
    onDelete(contactIndex);
  }, [onDelete, contactIndex]);

  return (
    <ContactEditRowGrid>
      <Controller
        name={`${fieldPrefix}.name` as const}
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <>
            <ContactEditLabelSlot $col={1}>
              <ValidationLabelContentWrapper>
                <ValidationTitle $isRequired={true}>Name</ValidationTitle>
              </ValidationLabelContentWrapper>
            </ContactEditLabelSlot>
            <ContactEditInputSlot $col={1}>
              <ValidationInputWrapper aria-required $isValid={!fieldState.error}>
                <InputText inputRef={ref} placeholder="Name" {...field} value={field.value ?? ""} />
              </ValidationInputWrapper>
            </ContactEditInputSlot>
            <ContactEditErrorSlot $col={1}>{renderErrorMessage(fieldState.error)}</ContactEditErrorSlot>
          </>
        )}
      />
      <Controller
        name={`${fieldPrefix}.email` as const}
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <>
            <ContactEditLabelSlot $col={2}>
              <ValidationLabelContentWrapper>
                <ValidationTitle $isRequired={true}>Email</ValidationTitle>
              </ValidationLabelContentWrapper>
            </ContactEditLabelSlot>
            <ContactEditInputSlot $col={2}>
              <ValidationInputWrapper aria-required $isValid={!fieldState.error}>
                <InputText inputRef={ref} placeholder="Email" {...field} value={field.value ?? ""} />
              </ValidationInputWrapper>
            </ContactEditInputSlot>
            <ContactEditErrorSlot $col={2}>{renderErrorMessage(fieldState.error)}</ContactEditErrorSlot>
          </>
        )}
      />
      <Controller
        name={`${fieldPrefix}.phone` as const}
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <>
            <ContactEditLabelSlot $col={3}>
              <ValidationLabelContentWrapper>
                <ValidationTitle $isRequired={true}>Phone</ValidationTitle>
              </ValidationLabelContentWrapper>
            </ContactEditLabelSlot>
            <ContactEditInputSlot $col={3}>
              <ValidationInputWrapper aria-required $isValid={!fieldState.error}>
                <InputMask
                  id={`${fieldPrefix}.phone`}
                  ref={ref}
                  mask="(999) 999-9999"
                  placeholder="(999) 999-9999"
                  {...field}
                  value={field.value ?? ""}
                />
              </ValidationInputWrapper>
            </ContactEditInputSlot>
            <ContactEditErrorSlot $col={3}>{renderErrorMessage(fieldState.error)}</ContactEditErrorSlot>
          </>
        )}
      />
      <Controller
        name={`${fieldPrefix}.title` as const}
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <>
            <ContactEditLabelSlot $col={4}>
              <ValidationLabelContentWrapper>
                <ValidationTitle $isRequired={false}>Title</ValidationTitle>
              </ValidationLabelContentWrapper>
            </ContactEditLabelSlot>
            <ContactEditInputSlot $col={4}>
              <ValidationInputWrapper $isValid={!fieldState.error}>
                <InputText inputRef={ref} placeholder="Title" {...field} value={field.value ?? ""} />
              </ValidationInputWrapper>
            </ContactEditInputSlot>
            <ContactEditErrorSlot $col={4}>{renderErrorMessage(fieldState.error)}</ContactEditErrorSlot>
          </>
        )}
      />
      <ContactEditDeleteSlot>
        <Button
          buttonType={ButtonType.Text}
          actionType={ButtonActionType.Error}
          icon={IconType.Delete}
          label="Delete"
          ariaLabel={deleteAriaLabel}
          onClick={handleDeleteRow}
        />
      </ContactEditDeleteSlot>
    </ContactEditRowGrid>
  );
};

const LocationDetailsContactsCardContent: FC<
  EditableDetailsCardChildProps<LocationContactsCardFormValues> & {
    location: Locality;
    contactsRaw: LocationContact[];
  }
> = ({ location, contactsRaw, ...cardProps }) => {
  const { handleSubmitAllFields, onSubmitForm, onEditRow, onSaveAllFields } = cardProps;
  const { fields, append, remove } = useFieldArray({
    control: cardProps.control,
    name: "contacts",
    /* RHF's default keyName is "id"; override to avoid shadowing any future `id` field on LocationContact. */
    keyName: "rhfKey",
  });

  const contactsForView = useMemo(() => mapLocationContactsForForm(contactsRaw), [contactsRaw]);
  const hasMultipleContactsValues = useMemo(
    () => isMultipleFieldValuesInAggregatedEntity(location, "contacts"),
    [location],
  );

  const onAddContact = useCallback(() => {
    append(defaultContact);
  }, [append]);

  const isLoading = useRootSelector(selectLocationsIsLoading);
  const locationsError = useRootSelector(selectLocationsError);
  const pendingSaveRef = useRef(false);
  const prevIsLoadingRef = useRef(isLoading);

  /* Exit edit mode only after the saga round-trip completes without error.
   * pendingSaveRef gates the effect so unrelated loading cycles are ignored.
   * On failure the saga already shows a toast; we stay in edit mode so the
   * user can correct and retry without having to re-enter edit mode. */
  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && pendingSaveRef.current) {
      pendingSaveRef.current = false;
      if (!locationsError) {
        onSaveAllFields();
      }
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, locationsError, onSaveAllFields]);

  /**
   * NOTE: The id is not carried over to the sent payload of contacts. The server
   * will assign a new id to the contacts stored in the database.
   */
  const onSaveContacts = useCallback(() => {
    void handleSubmitAllFields(data => {
      onSubmitForm(data);
      pendingSaveRef.current = true;
    })();
  }, [handleSubmitAllFields, onSubmitForm]);

  const onEditContacts = useMemo(() => onEditRow("contacts"), [onEditRow]);

  return (
    <DetailsCard
      title="Contacts"
      icon="user"
      actions={
        <DetailsCardEditActionsMenu
          isDirty={cardProps.isDirty}
          isEditing={cardProps.isEditing}
          hasValidationErrors={!cardProps.isValid}
          isEditHidden={hasMultipleContactsValues}
          onCancel={cardProps.onCancelEdit}
          onEdit={onEditContacts}
          onReset={cardProps.onResetForm}
          onSubmit={onSaveContacts}
        />
      }
    >
      <ContactViewContainer>
        {cardProps.isEditing ? (
          fields.length ? (
            <>
              {fields.map((field, index) => (
                <LocationDetailsContactRow
                  key={field.rhfKey}
                  contactIndex={index}
                  control={cardProps.control}
                  onDelete={remove}
                />
              ))}
            </>
          ) : (
            <UnassignedLabel text="Location(s) has no contacts" />
          )
        ) : hasMultipleContactsValues ? (
          <MultipleValuesLabel />
        ) : contactsForView.length ? (
          <>
            {contactsForView.map((contact, index) => (
              <ContactViewRow key={`${contact.id}_${index}`}>
                <ContactViewCell>{contactFieldValueOrPlaceholder(contact.name)}</ContactViewCell>
                <ContactViewCell>{contactFieldValueOrPlaceholder(contact.email)}</ContactViewCell>
                <ContactViewCell>
                  {contactFieldValueOrPlaceholder(getContactPhoneDisplayValue(contact.phone))}
                </ContactViewCell>
                <ContactViewCell>{contactFieldValueOrPlaceholder(contact.title)}</ContactViewCell>
              </ContactViewRow>
            ))}
          </>
        ) : (
          <UnassignedLabel text="Location(s) has no contacts" />
        )}
        {cardProps.isEditing && (
          <Button className="align-self-start my-2" icon={IconType.Create} label="Add Contact" onClick={onAddContact} />
        )}
      </ContactViewContainer>
    </DetailsCard>
  );
};

type LocationDetailsContactsCardProps = {
  /** Optional at the boundary so a null-guard before hooks is type-safe; callers typically pass a loaded entity. */
  location?: Locality | null;
  /** Single-location details: read contacts from raw Redux entity (GET /locations/:id). */
  contactSourceLocationId?: number;
  onSubmit: (changedFields: DeepPartial<Locality>) => void;
};

const LocationDetailsContactsCardInner: FC<{
  location: Locality;
  contactSourceLocationId?: number;
  onSubmit: (changedFields: DeepPartial<Locality>) => void;
}> = ({ location, contactSourceLocationId, onSubmit }) => {
  /*
   * Do not use useDeepRootSelector here: lodash isEqual can treat successive entity snapshots as "equal"
   * in edge cases and block updates, while reference equality reliably tracks Immer/RTK entity replaces
   * when `contacts` (or the row) is hydrated after navigation.
   */
  const contactsRaw = useLocationContactsRaw(location, contactSourceLocationId);

  /* `EditableDetailsCardDecorator` passes only dirty fields to onSubmit; field arrays mark per-row dirtiness,
   * so changedFields.contacts would omit untouched rows. resolveOnSubmit runs inside the decorator so we can
   * read full `contacts` via RHF's stable getValues. */
  const resolveContactsSubmit = useCallback(
    (
      getValues: UseFormGetValues<LocationContactsCardFormValues>,
    ): OnDeepPartialSubmitType<LocationContactsCardFormValues> =>
      _ => {
        const contactsForSubmit = getValues().contacts ?? [];
        const payload: DeepPartial<Locality> = {
          contacts: normalizeContactsForSubmit(contactsForSubmit),
        };
        onSubmit(payload);
      },
    [onSubmit],
  );

  const contactsFormDefaults = useMemo((): LocationContactsCardFormValues => {
    return { contacts: mapLocationContactsForForm(contactsRaw) };
  }, [contactsRaw]);

  /* Remount only when switching locations. Avoid a JSON-derived key on `contacts` data, which remounted
   * the whole form on any serialized change and could drop in-progress edits. Server-fed `contacts`
   * still flow through `initialValue`; `EditableDetailsCardDecorator` resets when that value changes. */
  return (
    <EditableDetailsCardDecorator<LocationContactsCardFormValues>
      key={location.id}
      initialValue={contactsFormDefaults}
      validationResolver={contactsYupResolver}
      resolveOnSubmit={resolveContactsSubmit}
    >
      {cardProps => <LocationDetailsContactsCardContent {...cardProps} location={location} contactsRaw={contactsRaw} />}
    </EditableDetailsCardDecorator>
  );
};

export const LocationDetailsContactsCard: FC<LocationDetailsContactsCardProps> = ({
  location,
  contactSourceLocationId,
  onSubmit,
}) => {
  if (location === undefined || location === null) {
    return null;
  }
  return (
    <LocationDetailsContactsCardInner
      location={location}
      contactSourceLocationId={contactSourceLocationId}
      onSubmit={onSubmit}
    />
  );
};
